/******************************************************************************
 * FILE:
 * src/modules/expenseManagement/workflow/hooks/useApprovalMatrix.js
 *
 * MODULE:
 * Expense Management
 *
 * FEATURE:
 * Approval Matrix
 *
 * PURPOSE:
 * React Hook for Approval Matrix Management
 *
 * RESPONSIBILITY:
 * • Load Approval Matrix Rules
 * • Load Approval Matrix Details
 * • Create Approval Matrix
 * • Update Approval Matrix
 * • Delete Approval Matrix
 * • Activate Approval Matrix
 * • Deactivate Approval Matrix
 * • Load Lookups
 *
 * ARCHITECTURE:
 * • No UI
 * • No JSX
 * • Uses approvalMatrixService
 ******************************************************************************/

import {
    useCallback,
    useState,
} from "react";

import approvalMatrixService
from "../services/approvalMatrixService";

import workflowMasterService
from "../services/workflowMasterService";

import expenseMasterService
from "../../claims/services/expenseMasterService";

export default function useApprovalMatrix() {

    const [loading, setLoading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const [error, setError] =
        useState(null);

    const [approvalMatrices, setApprovalMatrices] =
        useState([]);

    const [approvalMatrix, setApprovalMatrix] =
        useState(null);

    const [workflows, setWorkflows] =
        useState([]);

    const [approvalLevels, setApprovalLevels] =
        useState([]);

    const [departments, setDepartments] =
        useState([]);

    /**
     * ------------------------------------------------------------------------
     * LOAD APPROVAL MATRIX
     * ------------------------------------------------------------------------
     */

    const loadApprovalMatrices =
        useCallback(
            async (params = {}) => {

                try {

                    setLoading(true);

                    setError(null);

                    const data =
                        await approvalMatrixService
                            .getApprovalMatrices(params);

                    setApprovalMatrices(data);

                    return data;

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to load Approval Matrix.";

                    setError(message);

                    throw err;

                } finally {

                    setLoading(false);

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * LOAD ONE RULE
     * ------------------------------------------------------------------------
     */

    const loadApprovalMatrix =
        useCallback(
            async (approvalMatrixId) => {

                try {

                    setLoading(true);

                    setError(null);

                    const data =
                        await approvalMatrixService
                            .getApprovalMatrix(
                                approvalMatrixId
                            );

                    setApprovalMatrix(data);

                    return data;

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to load Approval Matrix.";

                    setError(message);

                    throw err;

                } finally {

                    setLoading(false);

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * LOAD WORKFLOWS
     * ------------------------------------------------------------------------
     */

    const loadWorkflows =
        useCallback(
            async () => {

                try {

                    const data =
                        await workflowMasterService
                            .getWorkflows();

                    setWorkflows(data);

                    return data;

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to load Workflows.";

                    setError(message);

                    throw err;

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * LOAD APPROVAL LEVELS
     * ------------------------------------------------------------------------
     */

    const loadApprovalLevels =
        useCallback(
            async () => {

                try {

                    const data =
                        await expenseMasterService
                            .listApprovalLevels();

                    setApprovalLevels(data);

                    return data;

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to load Approval Levels.";

                    setError(message);

                    throw err;

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * LOAD DEPARTMENTS
     * ------------------------------------------------------------------------
     */

    const loadDepartments =
        useCallback(
            async () => {

                try {

                    const data =
                        await expenseMasterService
                            .listDepartments();

                    setDepartments(data);

                    return data;

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to load Departments.";

                    setError(message);

                    throw err;

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * CREATE
     * ------------------------------------------------------------------------
     */

    const createApprovalMatrix =
        useCallback(
            async (payload) => {

                try {

                    setSaving(true);

                    setError(null);

                    return await approvalMatrixService
                        .createApprovalMatrix(payload);

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to create Approval Matrix.";

                    setError(message);

                    throw err;

                } finally {

                    setSaving(false);

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * UPDATE
     * ------------------------------------------------------------------------
     */

    const updateApprovalMatrix =
        useCallback(
            async (
                approvalMatrixId,
                payload
            ) => {

                try {

                    setSaving(true);

                    setError(null);

                    return await approvalMatrixService
                        .updateApprovalMatrix(
                            approvalMatrixId,
                            payload
                        );

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to update Approval Matrix.";

                    setError(message);

                    throw err;

                } finally {

                    setSaving(false);

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * DELETE
     * ------------------------------------------------------------------------
     */

    const deleteApprovalMatrix =
        useCallback(
            async (approvalMatrixId) => {

                try {

                    setSaving(true);

                    setError(null);

                    return await approvalMatrixService
                        .deleteApprovalMatrix(
                            approvalMatrixId
                        );

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to delete Approval Matrix.";

                    setError(message);

                    throw err;

                } finally {

                    setSaving(false);

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * ACTIVATE
     * ------------------------------------------------------------------------
     */

    const activateApprovalMatrix =
        useCallback(
            async (approvalMatrixId) => {

                try {

                    setSaving(true);

                    setError(null);

                    return await approvalMatrixService
                        .activateApprovalMatrix(
                            approvalMatrixId
                        );

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to activate Approval Matrix.";

                    setError(message);

                    throw err;

                } finally {

                    setSaving(false);

                }

            },
            []
        );

    /**
     * ------------------------------------------------------------------------
     * DEACTIVATE
     * ------------------------------------------------------------------------
     */

    const deactivateApprovalMatrix =
        useCallback(
            async (approvalMatrixId) => {

                try {

                    setSaving(true);

                    setError(null);

                    return await approvalMatrixService
                        .deactivateApprovalMatrix(
                            approvalMatrixId
                        );

                } catch (err) {

                    const message =
                        err?.response?.data?.message
                        || err.message
                        || "Failed to deactivate Approval Matrix.";

                    setError(message);

                    throw err;

                } finally {

                    setSaving(false);

                }

            },
            []
        );

    return {

        loading,

        saving,

        error,

        approvalMatrices,

        approvalMatrix,

        workflows,

        approvalLevels,

        departments,

        loadApprovalMatrices,

        loadApprovalMatrix,

        loadWorkflows,

        loadApprovalLevels,

        loadDepartments,

        createApprovalMatrix,

        updateApprovalMatrix,

        deleteApprovalMatrix,

        activateApprovalMatrix,

        deactivateApprovalMatrix,

    };

}